import { SanityPost } from "@/types/sanityPost";
import { Box, Link, Typography, linkClasses } from "@mui/material";
import { PortableTextBlock } from "@portabletext/types";
import React, { FC } from "react";

export const ContentTable: FC<ContentTableProps> = ({ content }) => {
  const relevantSections = Array.isArray(content)
    ? content.filter(
        (item): item is PortableTextBlock =>
          item.style && ["h2", "h3", "h4", "h5"].includes(item.style)
      )
    : [];

  const rawHeadings = relevantSections.reduce<
    {
      main: PortableTextBlock;
      children: PortableTextBlock[];
    }[]
  >(
    // eslint-disable-next-line max-params
    (acc, curr, idx) => {
      const latestGroup = acc.at(-1);

      const currentHeadingLesserThanPrevious =
        latestGroup &&
        latestGroup.main.style &&
        curr.style &&
        latestGroup.main.style < curr.style;

      if (!idx || !currentHeadingLesserThanPrevious) {
        return [
          ...acc,
          {
            main: curr,
            children: []
          }
        ];
      }

      if (currentHeadingLesserThanPrevious) {
        acc.at(-1)?.children.push(curr);
        return acc;
      }

      return acc;
    },
    []
  );

  const headings: { main: string; children: string[] }[] = rawHeadings
    .filter(({ main }) => main.children?.[0]?.text)
    .map(({ main, children }) => {
      const childHeadings = children
        .filter(child => child.children?.[0]?.text)
        .map(child => child.children[0].text);

      return {
        main: main.children[0].text,
        children: childHeadings
      };
    });

  const getHeadingSlug = (heading: string) =>
    heading.toLocaleLowerCase().split(" ").join("-");

  return relevantSections.length ? (
    <Box
      component="ol"
      sx={{
        pl: 0,
        my: { xs: 3, sm: 6 },
        listStyle: "none",
        borderTop: ({ palette }) => `1px solid ${palette.divider}`,
        borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
        [`& .${linkClasses.root}`]: {
          fontWeight: 700,
          color: ({ palette }) => palette.text.disabled,
          textDecoration: "none",
          transition: ({ transitions }) =>
            transitions.create("color", {
              easing: transitions.easing.sharp,
              duration: transitions.duration.shortest
            }),
          "&:hover": {
            color: ({ palette }) => ({
              xs: palette.text.disabled,
              sm: palette.primary.main
            })
          }
        }
      }}
    >
      {headings.map(({ main, children }, idx) => (
          <Box
            component="li"
            key={idx}
            sx={{
              py: 1,
              display: "grid",
              gridTemplateColumns: "24px 1fr",
              justifyItems: "start",
              columnGap: 1,
              fontSize: { xs: "0.75rem", sm: "1rem" },
              lineHeight: { xs: 1.35, sm: 1.5 },
              "&:not(:last-of-type)": {
                borderBottom: ({ palette }) => `1px solid ${palette.divider}`
              }
            }}
          >
            <Typography
              sx={{
                fontSize: "inherit",
                lineHeight: "inherit",
                textAlign: "center",
                justifySelf: "center",
                color: ({ palette }) => palette.primary.main,
                fontWeight: 700
              }}
            >
              {idx + 1}
            </Typography>
            <Link href={`#${getHeadingSlug(main)}`}>{main}</Link>
            {!!children.length && (
              <Box
                component="ol"
                sx={{
                  listStyle: "none",
                  pl: 1,
                  gridColumn: 2,
                  lineHeight: 1
                }}
              >
                {children.map((child, idx) => (
                  <Box sx={{ mt: 1 }} component="li" key={idx}>
                    <Link
                      sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.5
                      }}
                      href={`#${getHeadingSlug(child)}`}
                    >
                      {child}
                    </Link>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
    </Box>
  ) : null;
};

export interface ContentTableProps {
  content: SanityPost["_rawBody"];
}
